import React from 'react';

import {
  Box,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
} from '@mui/material';

import { Search } from '@mui/icons-material';

import { getNextSortState } from '../helpers/application-grid/sorting';
import { IDataGridColumn } from '../interfaces/IDataGridColumn';
import { ISortState } from '../interfaces/ISortState';
import { ApplicationGridSortIcon } from './ApplicationGridSortIcon';

interface IKeyValMap {
  [key: string]: any;
}

type TColumnsResolver = (
  col: IDataGridColumn,
  row: IKeyValMap,
) => React.ReactNode | undefined;

interface ApplicationListGridProps {
  rows: IKeyValMap[];
  columns: IDataGridColumn[];
  columnsResolver?: TColumnsResolver;
  expandableRows?: boolean;
  sortState: ISortState;
  setSortState: React.Dispatch<React.SetStateAction<ISortState>>;
}

/**
 * ApplicationListGrid Component
 */
export const ApplicationListGrid: React.FC<ApplicationListGridProps> = ({
  rows,
  columns,
  columnsResolver,
  sortState,
  setSortState,
}) => {
  const handleSortRequest = async (columnName: string) => {
    setSortState(prev => {
      return getNextSortState({
        currentSortState: prev,
        fieldToBeSorted: columnName,
      });
    });
  };

  const renderCellContent = (col: IDataGridColumn, row: IKeyValMap) => {
    const resolvedValue = columnsResolver
      ? columnsResolver(col, row)
      : undefined;
    return resolvedValue !== undefined ? resolvedValue : row[col.name];
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <TableContainer
        component={Paper}
        sx={{ flex: '1 1 auto', overflowX: 'auto' }}
      >
        <Table
          stickyHeader
          sx={{
            minWidth: {
              xs: `calc(${columns.length} * 150px)`,
              xl: `calc(${columns.length} * 7vw)`,
            },
          }}
        >
          <TableHead>
            <TableRow>
              {columns.map(col => (
                <TableCell
                  key={col.name}
                  sx={{
                    whiteSpace: 'nowrap',
                    minWidth: col.width || '150px',
                  }}
                >
                  {col.sortable ? (
                    <TableSortLabel
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                      }}
                      active={sortState.field === col.name}
                      direction={
                        sortState.order === 'ASC'
                          ? 'asc'
                          : sortState.order === 'DESC'
                            ? 'desc'
                            : undefined
                      }
                      onClick={() => handleSortRequest(col.name)}
                      data-key={col.name}
                      IconComponent={() => (
                        <ApplicationGridSortIcon
                          columnName={col.name}
                          sortState={sortState}
                        />
                      )}
                    >
                      <span
                        style={{ display: 'inline-block', lineHeight: '100%' }}
                      >
                        {col.labelCopy?.toUpperCase() || col.name.toUpperCase()}
                      </span>
                    </TableSortLabel>
                  ) : (
                    col.labelCopy?.toUpperCase() || col.name.toUpperCase()
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  align="center"
                  sx={{ py: 8 }}
                >
                  <Stack
                    spacing={1}
                    alignItems="center"
                    sx={{ color: 'text.secondary' }}
                  >
                    <Search sx={{ fontSize: '2rem', opacity: 0.7 }} />
                    No data found.
                  </Stack>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row, rowIndex) => (
                <TableRow key={rowIndex}>
                  {columns.map((col, colIndex) => (
                    <TableCell key={colIndex}>
                      {renderCellContent(col, row)}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};
